import { getDistinctFilteredForms } from "js/formatting/getDistinctFilteredForms";
import { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { Dialog } from "@headlessui/react";
import { getPoliciesOptions } from "js/formatting";
import {
  isCSR,
  isCSRAdmin,
  isPolicyInactiveByPolicyStatus,
} from "js/utilities";
import { getFormsByCategories } from "service/forms/getCustomerForms";
import { useRowPagination } from "hooks/useRowPagination";
import EverlakeLoadingOverlay from "components/General/EverlakeLoadingOverlay";
import SimpleDataRow from "components/General/SimpleDataRow";
import FormInputError from "components/General/FormInputError";
import InputWithActionButton from "components/General/InputWithActionButton";
import PillWithActionButton from "components/General/PillWithActionButton";
import config from "config/config";
import RELEASING_AutoCompleteComboBox from "components/General/AutoCompleteComboBox/RELEASING_AutoCompleteComboBox";
import CustomerRowPagination from "components/General/CustomerRowPagination";
import DialogBackdrop from "components/ModalDialogs/DialogBackdrop";
import { sendEmailNotification_v2 } from "service/notificationservice/sendEmailNotification";

export default function SearchByPolicyPanel() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useSelector((state) => state.user);
  const { csrUser } = useSelector((state) => state.csrUser);
  const contractIdFromUrl = searchParams.get("contractId");
  const keywordFromUrl = searchParams.get("keyword");

  const {
    reset,
    handleSubmit,
    control,
    clearErrors,
    formState: { errors },
  } = useForm();

  const { policies } = useSelector((state) => state.data);

  const [isLoading, setIsLoading] = useState(false);
  const [allPolicyIdOptions, setAllPolicyIdOptions] = useState([]);
  const [selectedPolicyId, setSelectedPolicyId] = useState({
    value: "all",
    displayAs: `${t("AutoComplate-select-your-contract")}`,
    selectedPolicyDisplayAs: `${t("AutoComplate-select-your-contract")}`,
  });
  const [allForms, setAllForms] = useState([]);
  const [selectedPolicyForms, setSelectedPolicyForms] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchByKeyWords, setSearchByKeyWords] = useState([]);
  const [isSingleInactivePolicy, setIsSingleInactivePolicy] = useState(false);
  const [isSendEmailDialogOpen, setIsSendEmailDialogOpen] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [sendEmailError, setSendEmailError] = useState("");
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [sendEmailSuccessMessage, setSendEmailSuccessMessage] = useState("");
  const [selectedFormId, setSelectedFormId] = useState(null);
  const isCSRUser = isCSR(csrUser) || isCSRAdmin(csrUser);

  const getPolicies = () => {
    const policySelectionArray = getPoliciesOptions(policies);
    let policiesOptions = [];

    policySelectionArray.map((policy) => {
      policiesOptions.push({
        ...policy,
        isInActive: isPolicyInactiveByPolicyStatus(policy?.status),
        selectedPolicyDisplayAs: `${policy.value}`,
        status: policy?.status,
      });
    });

    setAllPolicyIdOptions([selectedPolicyId, ...policiesOptions]);
    if (policies?.length === 1) setSelectedPolicyId(policiesOptions[0]);
  };

  async function getForms() {
    setIsLoading(true);
    setIsSingleInactivePolicy(false);
    const selectedPolicy = policies.find(
      (e) => e?.oldPolicy === selectedPolicyId.value
    );

    if (isPolicyInactiveByPolicyStatus(selectedPolicy?.status)) {
      setIsSingleInactivePolicy(true);
      setIsLoading(false);
      setSelectedPolicyForms([]);
      setAllForms([]);
      setCategoryList([]);
      setSelectedCategory("all");
      setSearchByKeyWords([]);
      return;
    }

    const insuredState = selectedPolicy.parties.filter(
      (party) => party?.person?.type?.toLowerCase() === "insured"
    );
    const residentState = insuredState.map((e) => e.address.state).join(",");
    const ALLOWED_TEMPLATED_IDS_FORMS_FOR_ANNUITY_PAYOUT_POLICY =
      config.ALLOWED_TEMPLATED_IDS_FORMS_FOR_ANNUITY_PAYOUT_POLICY.split(",");
    await getFormsByCategories(
      selectedPolicy?.oldPolicy,
      selectedPolicy?.blockCode,
      selectedPolicy?.issueState,
      residentState,
      selectedPolicy?.product?.type,
      user?.userId
    )
      .then((respone) => {
        if (respone.form?.length > 0) {
          let formArray = respone.form;
          if (
            selectedPolicy?.annuityPayoutIndicatorFlag?.toUpperCase() === "Y"
          ) {
            formArray = respone.form.filter((f) =>
              ALLOWED_TEMPLATED_IDS_FORMS_FOR_ANNUITY_PAYOUT_POLICY.includes(
                f.templateId
              )
            );
          }
          setSelectedPolicyForms(formArray);
          setAllForms(formArray);
          setCategoryList([...new Set(formArray.map((item) => item.category))]);
          filteredForms(formArray, selectedCategory, searchByKeyWords);
          setIsLoading(false);
        } else {
          setIsLoading(false);
          setSelectedPolicyForms([]);
        }
      })
      .catch((error) => {
        setIsLoading(false);
        console.log("Error", error);
      });
  }

  const filterByKeyWords = (form, keywords) => {
    let formFound;
    keywords.forEach((keyWord) => {
      let splitKeyWords = keyWord.split(/\s+/g);
      splitKeyWords.forEach((sk) => {
        if (form.keywords.toLowerCase().includes(sk.toLowerCase()))
          formFound = form;
      });
    });
    return formFound;
  };

  const filteredForms = (forms, category, keywords) => {
    if (selectedPolicyId.value !== "all" && forms?.length > 0) {
      let filterFormsBasedOnSelection = [...forms];

      if (filterFormsBasedOnSelection?.length > 0) {
        if (category && category !== "all") {
          filterFormsBasedOnSelection = filterFormsBasedOnSelection.filter(
            (form) => form.category === category
          );
        }
        if (keywords?.length > 0 && filterFormsBasedOnSelection?.length > 0) {
          filterFormsBasedOnSelection = filterFormsBasedOnSelection.filter(
            (form) => {
              let formFound = filterByKeyWords(form, keywords);
              if (formFound) return formFound;
            }
          );
        }
      }
      setSelectedPolicyForms(
        getDistinctFilteredForms(filterFormsBasedOnSelection)
      );
    }
  };

  const onSubmit = async (data, e) => {
    let keyWord = data?.keyword?.trim();
    if (keyWord) {
      if (!searchByKeyWords.includes(keyWord))
        setSearchByKeyWords((current) => [...current, keyWord]);
      e.target.reset();
    }
  };

  const removeSearchKeyWord = (search, value) => {
    const index = search.indexOf(value);
    if (index > -1) {
      search.splice(index, 1);
    }
    return search;
  };

  const removeKeyWord = (search) => {
    let value = removeSearchKeyWord(searchByKeyWords, search);
    if (value?.length > 0) setSearchByKeyWords((curr) => [...curr]);
    else setSearchByKeyWords([]);
    reset();
    document.getElementById("Site Search").value = "";
  };

  const closeSendEmailDialog = () => {
    setIsSendEmailDialogOpen(false);
    setRecipientEmail("");
    setSendEmailError("");
    setSendEmailSuccessMessage("");
    setSelectedFormId(null);
  };

  const handleSendEmail = async () => {
    const trimmedEmail = recipientEmail.trim();
    const emailPattern = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/;

    if (!trimmedEmail) {
      setSendEmailError("Email is required.");
      return;
    }

    if (!emailPattern.test(trimmedEmail)) {
      setSendEmailError("Please enter a valid email address.");
      return;
    }

    setIsSendingEmail(true);
    setSendEmailError("");
    setSendEmailSuccessMessage("");

    try {
      await sendEmailNotification_v2(trimmedEmail, selectedFormId);
      setSendEmailSuccessMessage("Email sent successfully.");
      setRecipientEmail("");
    } catch (error) {
      setSendEmailError("Failed to send email.");
    } finally {
      setIsSendingEmail(false);
    }
  };

  useEffect(() => {
    if (keywordFromUrl) {
      setSearchByKeyWords([keywordFromUrl]);
    }
  }, [keywordFromUrl]);

  useEffect(() => {
    getPolicies();
  }, []);

  useEffect(() => {
    if (contractIdFromUrl) {
      setSelectedPolicyId({ value: contractIdFromUrl });
    } else if (policies.length === 1) {
      setSelectedPolicyId({ value: policies[0].oldPolicy });
    }
  }, [policies, contractIdFromUrl]);

  useEffect(() => {
    if (selectedPolicyId?.value && selectedPolicyId?.value !== "all") {
      getForms();
    } else {
      setSelectedCategory("all");
      setAllForms([]);
      setSelectedPolicyForms([]);
      if (searchByKeyWords.length > 0) {
        setSearchByKeyWords([]);
      }
    }
    clearErrors();
  }, [selectedPolicyId]);

  useEffect(() => {
    filteredForms(allForms, selectedCategory, searchByKeyWords);
    clearErrors();
  }, [selectedCategory, searchByKeyWords]);

  const {
    rowsForDisplay: searchbyPolicyFormsRows,
    rowPaginationProps: searchbyPolicyFormsPaginationProps,
  } = useRowPagination(selectedPolicyForms);

  return (
    <EverlakeLoadingOverlay
      isLoading={isLoading}
      loadingText={t("CustomerForms-loading-text")}
    >
      <div className="p-2 md:p-4 bg-white rounded-lg shadow-[2px_4px_4px_0_rgba(0,0,0,0.16)]">
        <div className="grid gap-2 md:gap-x-4 md:grid-cols-[2fr,1fr,2fr]">
          <RELEASING_AutoCompleteComboBox
            labelName={t("CustomerForms-select-policy")}
            inputPlaceholderText={t("AutoComplate-select-your-contract")}
            options={allPolicyIdOptions}
            tooltipVerbiage={
              policies?.length === 1
                ? null
                : t("CustomerFACStepOne-tooltip-verbiage")
            }
            selectedOption={selectedPolicyId}
            setSelectedOption={setSelectedPolicyId}
            readOnly={policies?.length === 1}
            showBadge={true}
          />
          {selectedPolicyId.value !== "all" && categoryList?.length ? (
            <>
              <div>
                <RELEASING_AutoCompleteComboBox
                  labelName={t("CustomerForms-category")}
                  inputPlaceholderText={t(
                    "CustomerForms-category-inputPlaceholderText"
                  )}
                  options={[
                    {
                      value: "all",
                      displayAs: t("CustomerForms-all-category"),
                      selectedPolicyDisplayAs: t("CustomerForms-all-category"),
                    },
                    ...categoryList.map((category) => ({
                      value: category,
                      displayAs: category,
                      selectedPolicyDisplayAs: category,
                    })),
                  ]}
                  selectedOption={selectedCategory}
                  setSelectedOption={(option) =>
                    setSelectedCategory(option.value)
                  }
                />
              </div>
              <form
                onSubmit={handleSubmit(onSubmit)}
                className="flex flex-col-reverse"
              >
                <Controller
                  name="keyword"
                  control={control}
                  render={({ field: { onChange } }) => (
                    <InputWithActionButton
                      labelName="Keyword"
                      buttonType="submit"
                      buttonLabel={t("CustomerForms-searchButton")}
                      ariaLabel={t("CustomerForms-searchAriaLabel")}
                      placeholder={t("CustomerForms-searchPlaceholder")}
                      error={t("CustomerForms-searchErroressage")}
                      onChange={onChange}
                    />
                  )}
                  rules={{
                    pattern: {
                      value: /^[A-Za-z0-9() \-]+$/,
                      message: t("CustomerForms-pattern-error-display"),
                    },
                    required: t("CustomerForms-searchErroressage"),
                  }}
                />
              </form>
              {errors?.keyword?.message && (
                <>
                  <div></div>
                  <div></div>
                  <FormInputError errorMessage={errors?.keyword?.message} />
                </>
              )}
            </>
          ) : (
            <></>
          )}
        </div>
        {searchByKeyWords?.length && !isLoading ? (
          <div className="mt-4 flex flex-start gap-2 ">
            {searchByKeyWords.map((search, idx) => (
              <PillWithActionButton
                key={`search for forms - ${idx}`}
                label={search}
                action={() => removeKeyWord(search)}
              />
            ))}
          </div>
        ) : (
          <></>
        )}
      </div>
      <Dialog
        open={isSendEmailDialogOpen}
        onClose={closeSendEmailDialog}
        className="ui-dialogs"
      >
        <DialogBackdrop />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel
            role="dialog"
            aria-modal="true"
            className="ui-default-dialog max-w-md w-full"
          >
            <Dialog.Title className="font-body text-xl font-bold text-left">
              Send Email
            </Dialog.Title>
            <button
              className="ui-default-dialog-close"
              aria-label="close send email dialog"
              onClick={closeSendEmailDialog}
            >
              <svg
                className="ui-icon h-3.5 w-3.5"
                focusable="false"
                aria-hidden="true"
                role="presentation"
              >
                <use href="#ui-icon-close"></use>
              </svg>
            </button>
            <hr />
            <div className="ui-dialog-body">
              <label
                id="send_email_input_label"
                htmlFor="send_email_input"
                className="block mb-2"
              >
                Recipient's Email Address
              </label>
              <input
                id="send_email_input"
                type="email"
                aria-labelledby="send_email_input_label"
                className="mt-1"
                value={recipientEmail}
                onChange={(e) => {
                  setRecipientEmail(e.target.value);
                  setSendEmailError("");
                  setSendEmailSuccessMessage("");
                }}
                disabled={isSendingEmail}
              />
              {sendEmailError ? (
                <FormInputError errorMessage={sendEmailError} />
              ) : null}
              {sendEmailSuccessMessage ? (
                <p className="mt-2 text-green-700">{sendEmailSuccessMessage}</p>
              ) : null}
            </div>
            <div className="block text-right mt-6">
              <button
                type="button"
                className="ui-btn-secondary ui-btn-min-w mt-4"
                onClick={closeSendEmailDialog}
                disabled={isSendingEmail}
              >
                Cancel
              </button>
              <button
                type="button"
                className="ui-btn-primary ui-btn-min-w mt-4 ml-2"
                onClick={handleSendEmail}
                disabled={isSendingEmail}
              >
                {isSendingEmail ? "Sending..." : "Send"}
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
      {isSingleInactivePolicy ? (
        <div className="w-full mt-8 mb-2 md:ml-6 text-center">
          <h3>{t("CustomerForms-no-forms-message")}</h3>
        </div>
      ) : searchbyPolicyFormsRows?.length > 0 ? (
        <div className="flex justify-center flex-col p-0 mt-2 container">
          {searchbyPolicyFormsRows?.map((form, idx) => (
            <SimpleDataRow
              key={`policy Changes Forms Company change - ${idx}`}
              data={form}
              type="form"
              hideFormSubTitle={true}
              isCSRUser={isCSRUser}
              onSendEmailClick={(form) => {
                setRecipientEmail("");
                setSendEmailSuccessMessage("");
                setSendEmailError("");
                setSelectedFormId(Number(form?.id));
                setIsSendEmailDialogOpen(true);
              }}
            />
          ))}
          <CustomerRowPagination {...searchbyPolicyFormsPaginationProps} />
        </div>
      ) : (
        <div className="w-full mt-4 mb-2 md:ml-6 text-center">
          {!isLoading && (
            <>
              {!selectedPolicyId ||
              selectedPolicyId.value === "all" ||
              selectedPolicyId.value === "" ? (
                <h3> {t("CustomerForms-displayMessage")}</h3>
              ) : (
                <h3>{t("CustomerForms-errorMessage")}</h3>
              )}
            </>
          )}
        </div>
      )}
    </EverlakeLoadingOverlay>
  );
}
